import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Elements } from '@stripe/react-stripe-js';
import cx from 'classnames';

import Button from '~/components/Button';
import FAQSection from '~/components/FAQSection';
import MarkDownRender from '~/components/Markdown/MarkDownRender';
import Typography from '~/components/Typography';
import useSubscriberCheckout from '~/hooks/useSubscriberCheckout';
import type { Publication } from '~/interfaces/publication';
import type { PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';
import TestimonialsSection from '~/pages/subscribe/TestimonialsSection';
import UpgradeForm from '~/pages/upgrade/components/UpgradeForm';

import FreeTrialForm from '../components/FreeTrialForm';
import GiftTab from '../components/GiftTab';
import NextPageButton from '../components/NextPageButton';
import PlanCards from '../components/PlanCards';

import Logo from '../../subscribe/MainSection/Logo';
import { PricingProvider } from '../_content/PricingContext';

interface Props {
  publication: Publication;
  upgradePage: UpgradePage;
  pricingDetails: PricingDetails;
  giftPricingDetails: PricingDetails;
  isInitialLoadFromSwarm: boolean;
}

const CustomUpgradePage = ({
  publication,
  upgradePage,
  pricingDetails,
  giftPricingDetails,
  isInitialLoadFromSwarm,
}: Props) => {
  const {
    nextPage,
    isInProgress,
    handleRedirectAfterPayment,
    stripeSessionsLoading,
    stripeSessionIds,
    stripeSession,
    isExternalCheckout,
    isCollectingBillingAddress,
    priceId,
    setPriceId,
    price,
    premiumOffer,
    cardlessPremiumOffer,
  } = useSubscriberCheckout({ publication, upgradePage, pricingDetails });

  const { t } = useTranslation();
  const shouldRenderTestimonialSection = upgradePage?.testimonials_section?.enabled;
  const shouldRenderFAQSection = upgradePage?.faq_section?.enabled;
  const backgroundStyle = upgradePage.main_bg_image_url
    ? { backgroundImage: `url(${upgradePage.main_bg_image_url})` }
    : {
        background: upgradePage.main_section.bg_color,
      };
  const [currentTab, setCurrentTab] = useState<'gift' | 'individual'>('individual');
  const hasGiftTiers = giftPricingDetails?.tiers?.length > 0;

  const renderRedirectText = () => {
    return (
      <div className="pt-8">
        <NextPageButton
          color={upgradePage.main_section.redirect_text_color}
          text={upgradePage.main_section.redirect_text}
          nextPage={nextPage}
          fontSize={upgradePage.main_section.redirect_text_font_size}
          fontFamily={upgradePage.main_section.redirect_text_font_family}
        />
      </div>
    );
  };

  return (
    <PricingProvider nameYourPriceDefaultAmount={upgradePage?.main_section?.name_your_price_default_amount || '1'}>
      <div
        style={backgroundStyle}
        className={cx(
          shouldRenderTestimonialSection || shouldRenderFAQSection ? '' : 'min-h-screen',
          'flex w-full justify-center px-2 py-12',
          upgradePage.main_bg_image_url && 'bg-cover bg-center bg-no-repeat',
        )}
      >
        <div className="mx-auto max-w-3xl lg:max-w-5xl xl:max-w-7xl">
          <div className="flex flex-col items-center space-y-2">
            {upgradePage.main_section.enable_logo && (
              <div className="mb-2">
                <Logo publication={publication} className="h-16 w-16" />
              </div>
            )}

            <div className="flex flex-col items-center space-y-1">
              {upgradePage.main_section.overline && (
                <Typography
                  as="span"
                  size={upgradePage.main_section.overline_font_size}
                  color={upgradePage.main_section.overline_color}
                  weight="bold"
                  fontFamily={upgradePage.main_section.overline_font_family}
                >
                  <MarkDownRender>{upgradePage?.main_section?.overline}</MarkDownRender>
                </Typography>
              )}
              <div className="flex flex-col items-center space-y-1.5">
                {upgradePage.main_section.headline && (
                  <Typography
                    as="h1"
                    size={upgradePage.main_section.headline_font_size}
                    color={upgradePage.main_section.headline_color}
                    weight="bold"
                    fontFamily={upgradePage.main_section.headline_font_family}
                    className={cx({
                      'w-full max-w-2xl break-words text-center sm:break-normal': true,
                    })}
                  >
                    <MarkDownRender>{upgradePage?.main_section?.headline}</MarkDownRender>
                  </Typography>
                )}
                {upgradePage.main_section.description && (
                  <Typography
                    as="div"
                    size={upgradePage.main_section.description_font_size}
                    color={upgradePage.main_section.description_color}
                    weight="regular"
                    fontFamily={upgradePage.main_section.description_font_family}
                    className={cx({
                      'w-full max-w-2xl text-center': true,
                    })}
                  >
                    <MarkDownRender>{upgradePage?.main_section?.description}</MarkDownRender>
                  </Typography>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center space-y-6 pt-6 lg:space-y-12">
              {hasGiftTiers && (
                <div className="flex flex-row space-x-6">
                  <Button
                    className="min-w-24"
                    onClick={() => setCurrentTab('individual')}
                    variant={currentTab === 'individual' ? 'primary' : 'secondary'}
                  >
                    {t('upgrade.tabs.individual.title')}
                  </Button>
                  <Button
                    className="min-w-24"
                    onClick={() => setCurrentTab('gift')}
                    variant={currentTab === 'gift' ? 'primary' : 'secondary'}
                  >
                    {t('upgrade.tabs.gift.title')}
                  </Button>
                </div>
              )}
              {currentTab === 'individual' && (
                <Elements stripe={stripeSession}>
                  <PlanCards
                    priceId={priceId}
                    setPriceId={setPriceId}
                    upgradePage={upgradePage}
                    pricingDetails={pricingDetails}
                    sessionIds={stripeSessionIds}
                    areSessionsLoading={stripeSessionsLoading}
                    isExternalCheckoutEnabled={isExternalCheckout}
                    isCollectingBillingAddress={isCollectingBillingAddress}
                    onContinue={handleRedirectAfterPayment}
                  />
                  {/*
                    When not using external checkout, we render the upgrade form if there is not a cardless free trial
                    offer. If there IS a cardless free trial offer, we only need to show the email and submit buttons.
                  */}
                  {!isExternalCheckout && !cardlessPremiumOffer && (
                    <UpgradeForm
                      priceId={priceId}
                      upgradePage={upgradePage}
                      pricingDetails={pricingDetails}
                      onContinue={handleRedirectAfterPayment}
                      publication={publication}
                      premiumOfferId={premiumOffer?.id}
                    />
                  )}
                  {!isExternalCheckout && premiumOffer && cardlessPremiumOffer && (
                    <div className="mx-auto max-w-2xl">
                      <FreeTrialForm
                        price={price}
                        premiumOfferId={premiumOffer.id}
                        upgradePage={upgradePage}
                        onContinue={handleRedirectAfterPayment}
                      />
                    </div>
                  )}
                </Elements>
              )}
              {currentTab === 'gift' && (
                <GiftTab upgradePage={upgradePage} giftPricingDetails={giftPricingDetails} publication={publication} />
              )}
            </div>

            {!shouldRenderTestimonialSection && isInProgress && renderRedirectText()}

            {upgradePage?.main_image_url && (
              <div className={cx('w-full border pt-20')}>
                <img
                  src={upgradePage?.main_image_url || ''}
                  alt="Main depiction of the publication on the landing page"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/** Testimonials Section */}
      {shouldRenderTestimonialSection && (
        <TestimonialsSection
          isUpgradePage
          testimonialsSection={upgradePage.testimonials_section}
          testimonials={upgradePage.upgrade_page_testimonials}
          redirectActionChild={isInProgress && <div className="pt-16">{renderRedirectText()}</div>}
        />
      )}

      {/** FAQ Section */}
      {shouldRenderFAQSection && (
        <FAQSection faq_section={upgradePage.faq_section} isFirstQuestionOpen={isInitialLoadFromSwarm} />
      )}
    </PricingProvider>
  );
};

export default CustomUpgradePage;
